import React, { FC } from "react";
import { useParams } from "react-router-dom";

import { eachDayOfInterval, format } from "date-fns";

import { toDateString, currentFormatTimestamp } from "~/libs";

import { arrayToCsv, downloadBlob } from "~/utils";

import {
  useDateRange,
  useMultiDivisionMultiSectionDropdown,
  useProvidersCurrentEmployee,
  useApisManagersEmployeeSurveysProgressIndex,
} from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";
import {
  MultiParentsWithMultiChildrenDropdownField,
  SelectDateRangeButtons,
  FilterWrapper,
  NoDataDisplay,
  RichHeader,
  ProgressTableForSurveyProgress,
} from "~/components/organisms";

import { Button, Label, RightSidebarTitle } from "~/ui";
import { TitleWithChild } from "~/ui";

export const ManagersEmployeeSurveysProgressShow: FC = () => {
  const { employeeSurveyId = "" } = useParams<{ employeeSurveyId: string }>();
  const { currentEmployee } = useProvidersCurrentEmployee();

  const [{ startDate, endDate, onChange }] = useDateRange([null, null]);

  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions,
    selectableSections: currentEmployee?.businessSections,
  });

  const { data: progressData } = useApisManagersEmployeeSurveysProgressIndex({
    employeeSurveyId,
    params: {
      startDate: startDate ? toDateString(startDate) : "",
      endDate: endDate ? toDateString(endDate) : "",
      businessDivisionIds: selectedDivisions.map((division) => division.id),
      businessSectionIds: optionSelectedSections.map((section) => section.id),
    },
    config: {
      enabled:
        !!startDate &&
        !!endDate &&
        selectedDivisions.length > 0 &&
        optionSelectedSections.length > 0,
    },
  });

  const handleCSVExport = () => {
    if (!progressData) return;
    const csvData = generateCsvData();
    downloadBlob(
      arrayToCsv(csvData),
      `${currentFormatTimestamp}-チェックアウト進捗一覧.csv`,
    );
  };

  const breadCrumbItems = [
    { label: "チェックアウト", href: "/managers/employee_surveys" },
    {
      label: "投稿一覧",
      href: `/managers/employee_surveys/${employeeSurveyId}/progress`,
    },
  ];

  const generateCsvData = () => {
    if (!startDate || !endDate || !progressData) return [];

    const dates = eachDayOfInterval({ start: startDate, end: endDate }).map(
      (d) => toDateString(d),
    );

    // ヘッダー
    const header = [
      "氏名",
      ...dates.map((date) => format(new Date(date), "MM/dd")),
    ];

    // 各ユーザーごとの行
    const rows: string[][] = progressData.userProgress.map((user) => {
      return [
        user.employee.name,
        ...dates.map((date) => {
          const progress = user.progress.find((p) => p.postDate === date);
          return (progress?.count || 0) > 0 ? "■" : "";
        }),
      ];
    });

    return [header, ...rows];
  };

  return (
    <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent withRightSidebar>
        {/* フィルター部分 */}
        <FilterWrapper>
          <Label
            labelText="期間指定"
            className="text-sm font-semibold text-secondary-600"
            required={false}
            htmlFor="dateRange"
          />
          <SelectDateRangeButtons
            startDate={startDate}
            endDate={endDate}
            onChange={onChange}
            className="w-full"
          />
          <MultiParentsWithMultiChildrenDropdownField
            allParents={selectableDivisions}
            parentsValue={selectedDivisions}
            parentLabel="部署"
            parentRequired
            parentsOnChange={onDivisionChange}
            allChildren={optionSelectableSections}
            childrenValue={optionSelectedSections}
            childLabel="課"
            childrenOnChange={onSectionChange}
            childRequired
          />
        </FilterWrapper>

        {startDate && endDate && progressData ? (
          <div className="mt-8 space-y-6">
            <RichHeader
              title="チェックアウト進捗"
              description="従業員のチェックアウト投稿状況を確認できます"
              icon="ioDocumentTextOutline"
            />
            <ProgressTableForSurveyProgress
              startDate={startDate}
              endDate={endDate}
              userProgress={progressData.userProgress}
            />
          </div>
        ) : (
          <NoDataDisplay message="期間と部署・課を選択してデータを表示してください" />
        )}
      </EmployeeLayoutMainContent>
      <EmployeeLayoutRightSidebar>
        <RightSidebarTitle title="CSV" />
        <div className="px-4 py-3 space-y-6">
          <TitleWithChild title="CSVをエクスポートする">
            <Button
              text="CSVエクスポート"
              color={"gray"}
              outline
              onClick={handleCSVExport}
              className="w-full"
            />
          </TitleWithChild>
        </div>
      </EmployeeLayoutRightSidebar>
    </EmployeeLayout>
  );
};
